/* eslint-disable max-lines */
/* eslint-disable react/forbid-elements */
/* eslint-disable @typescript-eslint/naming-convention */
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Creatable from 'react-select/creatable';
import { getTranslate } from '../../../common/src/translate/translateHelper';
import { useTranslate } from '../mixins/translate/translate';
import { assembleUrl } from '../store/assembleUrl';
import { reactSelectDefaultStyles } from '../utils/reactSelectDefaultStyles';
import { SortColumn, SortDirections, getSortOrder } from '../utils/sorting';
import { usePatchedDropdownMenu } from '../utils/usePatchedDropdownMenu';
import { appContext } from './appContext';
import { LocalizedDatePicker } from './common/LocalizedDatePicker';
import { Component } from './utils/Component';

export function Topbar(props: { children: React.ReactNode }) {
    return (
        <header id="topbar" className="ph10">
            {props.children}
        </header>
    );
}

export function TopbarLeft(props: { children: React.ReactNode }) {
    return (
        <div className="topbar-left">
            <ol className="breadcrumb">{props.children}</ol>
        </div>
    );
}

export function TopbarRight(props: { children: React.ReactNode }) {
    return <div className="topbar-right">{props.children}</div>;
}

export function TopbarLeftOnly(props: { children: React.ReactNode }) {
    return (
        <header id="topbar" className="ph10">
            <div className="topbar-left">
                <ol className="breadcrumb">{props.children}</ol>
            </div>
        </header>
    );
}

export function TopbarCrumbActive(props: { title?: string | undefined; icon: string }) {
    return (
        <li className="crumb-active">
            <h4>
                <span className={`fa ${props.icon} pr10`}></span>
                {props.title}
            </h4>
        </li>
    );
}

export function TopbarCrumb(props: { children: React.ReactNode }) {
    return <li className="crumb-trail">{props.children}</li>;
}

export function TopbarWithTabs(props: { title?: string; icon: string; children: React.ReactNode }) {
    return (
        <ul className="nav nav-list nav-list-topbar pull-left">
            <li className="pr20">
                <h4>
                    <span className={`fa ${props.icon} pr10`}></span>
                    {props.title}
                </h4>
            </li>
            {props.children}
        </ul>
    );
}

export function Tray(props: { padding?: boolean; children: React.ReactNode }) {
    const classValue = classNames({
        ph5: props.padding,
        pv10: props.padding,
        tray: true,
        'tray-center': true,
    });
    return <div className={classValue}>{props.children}</div>;
}

export function LeftTray(props: { children: React.ReactNode }) {
    return (
        <aside className="tray tray-left tray290" data-tray-height="match">
            {props.children}
        </aside>
    );
}

export function RightTray(props: { children: React.ReactNode }) {
    return (
        <aside className="tray tray-right tray290" data-tray-height="match">
            {props.children}
        </aside>
    );
}

export function LeftTrayList(props: { children: React.ReactNode }) {
    return <ul className="icon-list fs13 mt5 mb20 pn">{props.children}</ul>;
}

export function LeftTrayItem(props: { title?: string; children: React.ReactNode }) {
    return (
        <>
            {(props.title && (
                <li>
                    <i className="fa fa-caret-right text-warning fa-lg pr10"></i>
                    {props.title}:<span className="pl10 fw300">{props.children}</span>
                </li>
            )) || (
                <li>
                    <span className="fw600 fs14">{props.children}</span>
                </li>
            )}
        </>
    );
}

export function LeftTrayHeader(props: { children: React.ReactNode }) {
    return <h5 className="text-info mt20">{props.children}</h5>;
}

export function Content(props: { children: React.ReactNode }) {
    return (
        <section id="content" className="table-layout">
            {props.children}
        </section>
    );
}

export function Panel(props: { small?: boolean; topBorder?: boolean; children: React.ReactNode; id?: string }) {
    const classValue = classNames({
        mw700: props.small,
        mw1000: !props.small,
        'admin-form': true,
        'theme-primary ': true,
        'center-block': true,
    });
    const classValuePanel = classNames({
        panel: true,
        'heading-border': props.topBorder,
    });
    return (
        <div className={classValue} id={props.id}>
            <div className={classValuePanel}>{props.children}</div>
        </div>
    );
}

export function PanelWithoutForm(props: { children: React.ReactNode; id?: string }) {
    return (
        <div className="panel" id={props.id}>
            {props.children}
        </div>
    );
}

export function PanelBodyText(props: { text: string }) {
    return (
        <div className="panel-body">
            <div className="tab-content pn br-n text-dark">
                <p className="text-center">- {props.text} -</p>
            </div>
        </div>
    );
}

export function PanelBody(props: {
    isForm?: boolean;
    isHidden?: boolean;
    noPadding?: boolean;
    whiteBackground?: boolean;
    mb?: boolean;
    children?: React.ReactNode;
    id?: string;
}) {
    const classValue = classNames({
        'bg-light': !props.whiteBackground,
        'bg-white': props.whiteBackground,
        'admin-form': props.isForm,
        'panel-body': true,
        hidden: props.isHidden,
        pn: props.noPadding,
        mb10: props.mb,
    });

    return (
        <div className={classValue} id={props.id}>
            {props.children}
        </div>
    );
}

export function PanelHeading(props: { text: string; icon?: string; dark?: boolean; children?: React.ReactNode }) {
    const classValue = classNames({
        'panel-heading': true,
        'bg-light': true,
        dark: props.dark,
    });

    return (
        <div className={classValue}>
            {props.text !== '' && (
                <span className="panel-title">
                    {(props.icon && (
                        <>
                            <span className={props.icon} />
                            {props.text}
                        </>
                    )) || <span className="fw400 fs15">{props.text}</span>}
                </span>
            )}
            <div className="pull-right">{props.children}</div>
        </div>
    );
}

export function PanelBodyHeading(props: { text?: undefined | string; children?: undefined | React.ReactNode }) {
    return (
        <>
            <div className="panel-heading">
                <span className="panel-title">{props.text ?? ''}</span>
            </div>
            <div className="panel-body">{props.children}</div>
        </>
    );
}

export function PanelTopHeading(props: { children: React.ReactNode }) {
    return (
        <div className="panel-header">
            <div className="p25">
                <h2 className="fw200">{props.children}</h2>
            </div>
        </div>
    );
}

export function PanelHeader(props: { text?: undefined | string; fixWidth?: undefined | boolean }) {
    const classValue = classNames({
        'section-divider ': true,
        mt25: true,
        mb20: true,
        mw1000: props.fixWidth,
        dark: true,
    });
    return (
        <div className={classValue}>
            <span>{props.text}</span>
        </div>
    );
}

export function PanelHorizontalSpacing(props: { children: React.ReactNode }) {
    return <div className="pr20 pl20">{props.children}</div>;
}

export function PanelSpacing(props: { children: React.ReactNode }) {
    return <div className="p20">{props.children}</div>;
}

export function PanelTable(props: { hover?: boolean; children: React.ReactNode }) {
    const classValue = classNames({
        table: true,
        'table-hover': props.hover,
        'table-bordered': true,
        'table-condensed': true,
        'bg-light lighter': true,
    });
    return (
        <div className="table-responsive mb20 br-b">
            <table className={classValue}>{props.children}</table>
        </div>
    );
}

export function PanelSection(props: { children: React.ReactNode }) {
    return (
        <div className="row mt10">
            <div className="col-md-12">{props.children}</div>
        </div>
    );
}

export function DataPanel(props: { children: React.ReactNode }) {
    return (
        <>
            <td style={{ border: 'none' }}>&nbsp;</td>
            <td colSpan={7} style={{ borderLeft: '1px solid #f4f4f4' }}>
                <div className="dd-content ml10">
                    <div className="bg-light darker p10 br2 br-a br-greyer">{props.children}</div>
                </div>
            </td>
        </>
    );
}

export function Table(props: {
    hover?: boolean;
    sm?: boolean;
    xs?: boolean;
    spaced?: boolean;
    whiteBackground?: boolean;
    children: React.ReactNode;
}) {
    const classValue = classNames({
        table: true,
        mw400: props.xs,
        mw600: props.sm,
        'table-condensed': !props.spaced,
        'table-hover': props.hover,
        'bg-white': props.whiteBackground,
    });
    return <table className={classValue}>{props.children}</table>;
}

export function TableHead(props: { children: React.ReactNode }) {
    return <thead>{props.children}</thead>;
}

export function TableHeadRow(props: { light?: boolean; children: React.ReactNode }) {
    return <tr className={props.light ? 'bg-light' : 'bg-primary'}>{props.children}</tr>;
}

export function TableBody(props: { children: React.ReactNode }) {
    return <tbody>{props.children}</tbody>;
}

export function TableHeader(props: { children?: React.ReactNode; className?: string; align?: 'right' | undefined }) {
    return (
        <th className={props.className ? props.className : ''} style={props.align ? { textAlign: 'right' } : {}}>
            {props.children}
        </th>
    );
}

export function TableRow(props: { children: React.ReactNode; isRowSelected?: boolean | undefined; onClick?: undefined | (() => void) }) {
    return (
        <tr
            className={props.isRowSelected ? 'bg-light darker' : 'bg-white'}
            style={props.onClick ? { cursor: 'pointer' } : { cursor: 'default' }}
            onClick={props.onClick}
        >
            {props.children}
        </tr>
    );
}

export function TableData(props: {
    children?: React.ReactNode;
    alignRight?: boolean;
    alignCenter?: boolean;
    colSpan?: number;
    nowrap?: boolean;
    dark?: boolean;
    onClick?: () => void;
}) {
    const styleValue = {
        cursor: props.onClick ? 'pointer' : 'inherit',
    };
    const classValue = classNames({
        'text-right': props.alignRight,
        'text-center': props.alignCenter,
        nowrap: props.nowrap,
        'bg-light darker': props.dark,
    });

    if (props.colSpan) {
        return (
            <td style={styleValue} onClick={props.onClick} colSpan={props.colSpan} className={classValue}>
                {props.children}
            </td>
        );
    }
    return (
        <td style={styleValue} onClick={props.onClick} className={classValue}>
            {props.children ?? <>&nbsp;</>}
        </td>
    );
}

export function TableEmphasizedText(props: { text: string }) {
    return <strong className="text-primary-darker">{props.text}</strong>;
}

export function TableSubText(props: { text: string | null | undefined }) {
    if (!props.text) {
        return null;
    }
    return <div className="fs12 text-muted-darker">{props.text}</div>;
}

export function TableButtons(props: { children: React.ReactNode; large?: boolean }) {
    return (
        <td style={{ whiteSpace: 'nowrap', width: props.large ? '280px' : '120px' }} className="text-right">
            {props.children}
        </td>
    );
}

export function TableExpandButton(props: { open: boolean | undefined; onClick: () => void }) {
    const classValue = classNames({
        'fa fa-minus': props.open,
        'fa fa-plus': !props.open,
    });
    return (
        <span>
            <button
                onClick={(e) => {
                    e.preventDefault();
                    props.onClick();
                }}
                type="button"
                className="btn btn-primary btn-sm fs12"
            >
                <span className={classValue} />
            </button>
        </span>
    );
}

export function LimitTableWidth(props: { children: React.ReactNode; rows: number }) {
    return <div style={{ overflowX: 'scroll', maxWidth: props.rows > 8 ? '1500px' : `${185 * props.rows}px` }}>{props.children}</div>;
}

export function PanelButtons(props: { noBorderBottom?: boolean; children: React.ReactNode }) {
    const classValue = classNames({
        p12: true,
        'text-right': true,
        'br-b': !props.noBorderBottom,
    });

    return <div className={classValue}>{props.children}</div>;
}

export function TableSortButton(props: { onClick: () => void }) {
    return (
        <a
            href="#"
            onClick={(e) => {
                e.preventDefault();
                props.onClick();
            }}
            className="ml10"
        >
            <i className="fa fa-caret-down" />
        </a>
    );
}

export function TableSortableHeader<SortColumnName extends string = string>(props: {
    header: string;
    columnName: SortColumnName;
    sortColumns: SortColumn<SortColumnName>[];
    onClick: (columnName: SortColumnName, direction: SortDirections | undefined) => void;
    align?: 'right';
}) {
    const currentDirection = getSortOrder(props.columnName, props.sortColumns);

    let nextDirection: SortDirections | undefined;
    switch (currentDirection) {
        case 'ascending':
            nextDirection = SortDirections.Descending;
            break;
        case 'descending':
            nextDirection = undefined;
            break;
        default:
            nextDirection = SortDirections.Ascending;
    }
    let caretElement: JSX.Element | null;
    switch (currentDirection) {
        case 'ascending':
            caretElement = <i className="fa fa-caret-up ml10" />;
            break;
        case 'descending':
            caretElement = <i className="fa fa-caret-down ml10" />;
            break;
        default:
            caretElement = null;
    }

    return (
        <TableHeader align={props.align}>
            <a
                href="#"
                className="sort-header"
                onClick={() => {
                    props.onClick(props.columnName, nextDirection);
                }}
            >
                {' '}
                {props.header}
                {caretElement}
            </a>
        </TableHeader>
    );
}

export function TopBarDownloadButton(props: { path: string; text: string; disabled?: undefined | boolean; query?: undefined | Record<string, any> }) {
    const url = assembleUrl(props.path, props.query);
    return <PanelLink href={url} text={props.text} isDownload icon="fa fa-download" small disabled={props.disabled} />;
}

export function TopBarButton(props: {
    text: string;
    disabled?: undefined | boolean;
    icon?: string | undefined;
    small?: undefined | boolean;
    onClick: () => void;
}) {
    const className = classNames({ btn: true, 'btn-sm': props.small, 'btn-default': true, ml10: true, fw600: true });

    return (
        <button onClick={props.onClick} type="button" className={className} disabled={props.disabled}>
            {props.icon && <span className={`fa ${props.icon} mr5`}></span>}
            {props.text}
        </button>
    );
}

export function PanelLink(props: {
    href: string;
    text: string;
    icon?: undefined | string;
    small?: undefined | boolean;
    xsmall?: undefined | boolean;
    rightAllign?: undefined | boolean;
    primary?: undefined | boolean;
    isDownload?: undefined | boolean;
    disabled?: undefined | boolean;
}) {
    const classValue = classNames({
        btn: true,
        'btn-sm': props.small,
        'btn-xs': props.xsmall,
        'pull-right': props.rightAllign,
        'btn-default': !props.primary,
        'btn-primary': props.primary,
        pv2: props.xsmall,
        ml10: !props.xsmall,
        ml5: props.xsmall,
        mb5: false,
        fw600: true,
    });
    if (props.disabled) {
        return (
            <button className={classValue} disabled>
                {props.icon && <span className={`${props.icon} mr5`}></span>}
                {props.text}
            </button>
        );
    }
    return (
        <a href={props.href} className={classValue} download={props.isDownload}>
            {props.icon && <span className={`${props.icon} mr5`}></span>}
            {props.text}
        </a>
    );
}

export function PanelCreateLink(props: { to: string; text: string }) {
    return <PanelRouterLink to={props.to} text={props.text} icon="fa fa-plus" small={true} />;
}
export function PanelRouterLink(props: {
    to: string;
    text: string;
    icon?: string;
    small?: boolean;
    xsmall?: boolean;
    rightAllign?: boolean;
    primary?: boolean;
}) {
    const classValue = classNames({
        btn: true,
        'btn-sm': props.small,
        'btn-xs': props.xsmall,
        'pull-right': props.rightAllign,
        'btn-default': !props.primary,
        'btn-primary': props.primary,
        pv2: props.xsmall,
        ml10: !props.xsmall,
        ml5: props.xsmall,
        mb5: false,
        fw600: true,
    });
    return (
        <Link to={props.to} className={classValue}>
            {props.icon && <span className={`${props.icon} mr5`}></span>}
            {props.text}
        </Link>
    );
}

export function LinkButton(props: { children: React.ReactNode; className?: string; onClick: () => void }) {
    return (
        <a
            href="#"
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                props.onClick();
            }}
            className={props.className}
        >
            {props.children}
        </a>
    );
}

// used on top of panel or form
export function PanelButtonLink(props: {
    text?: string;
    icon?: string;
    small?: boolean;
    xsmall?: boolean;
    rightAllign?: boolean;
    primary?: boolean;
    mb0?: boolean;
    mt?: boolean;
    disabled?: boolean;
    onClick: () => void;
    id?: string;
    navigationTitles?: boolean;
}) {
    const classValue = classNames({
        btn: true,
        'btn-sm': props.small,
        'btn-xs': props.xsmall,
        'pull-right': props.rightAllign,
        'btn-default': !props.primary,
        'btn-primary': props.primary,
        pv2: props.xsmall,
        ml10: !props.xsmall,
        ml5: props.xsmall,
        mt5: props.mt,
        mb5: !props.mb0,
        fw600: true,
        disabled: props.disabled,
    });

    return (
        <LinkButton onClick={() => props.onClick()} className={classValue}>
            {props.icon && props.text && props.navigationTitles && (
                <>
                    {props.text}
                    <span className={`${props.icon} ml5`}></span>
                </>
            )}
            {props.icon && props.text && !props.navigationTitles && (
                <>
                    <span className={`${props.icon} mr5`}></span>
                    {props.text}
                </>
            )}
            {props.icon && !props.text && <span className={props.icon}></span>}
            {props.text && !props.icon && props.text}
        </LinkButton>
    );
}

// used on form
export function PanelButton(props: { text: string; small?: boolean; primary?: boolean; onClick: () => void; disabled?: boolean }) {
    const classValue = classNames({
        button: true,
        'btn-sm': props.small,
        'btn-default': !props.primary,
        'btn-primary': props.primary,
        fs14: true,
    });
    return (
        <button type="button" className={classValue} onClick={() => props.onClick()} disabled={props.disabled}>
            {props.text}
        </button>
    );
}

type CollapsePanelProps = {
    heading: {
        text: string;
        icon?: undefined | string;
        dark?: undefined | boolean;
        noDark?: undefined | boolean;
        initialIsClosed?: undefined | boolean;
    };
    children: React.ReactNode;
    name?: undefined | string;
};

export function CollapsiblePanel(props: CollapsePanelProps) {
    const translateKey = useTranslate();
    const [isOpen, setIsOpen] = React.useState(!props.heading.initialIsClosed);
    const faIconPatchedStyles = { fontSize: '14px/1', marginRight: 0 };
    const classes = classNames('panel-heading bg-light cursor', { dark: !props.heading.noDark });

    function handleClick(e: React.MouseEvent) {
        e.preventDefault();
        setIsOpen(!isOpen);
    }

    return (
        <>
            <div className={classes} onClick={(e) => handleClick(e)} style={{ padding: '0 8px' }}>
                <span className="panel-title" style={{ fontWeight: 400, fontSize: 'inherit', paddingLeft: '8px' }}>
                    <span className="mb10 mr20">
                        {isOpen ? (
                            <i className="fa fa-caret-down" style={faIconPatchedStyles} />
                        ) : (
                            <i className="fa fa-caret-right" style={faIconPatchedStyles} />
                        )}
                    </span>
                    <span className="fw500 fs14">{props.heading.text}</span>
                </span>
                <div className="pull-right">
                    {isOpen ? (
                        <div className="fs13 fw600">
                            <a href="" onClick={(e) => handleClick(e)}>
                                {translateKey('common.hide')}
                            </a>
                        </div>
                    ) : (
                        <div className="fs13 fw600">
                            <a href="" onClick={(e) => handleClick(e)} data-testid={`CollapsiblePanel-${props.name}-show`}>
                                {translateKey('common.show')}
                            </a>
                        </div>
                    )}
                </div>
            </div>
            {isOpen && <>{props.children}</>}
        </>
    );
}

export function PanelResultHeader(props: { indent?: boolean; children: React.ReactNode }) {
    const classValue = classNames({
        'text-primary': true,
        mt20: true,
        fs14: true,
        ml10: props.indent,
    });

    return <h4 className={classValue}>{props.children}</h4>;
}

export function PanelResult(props: { children: React.ReactNode }) {
    return <div className="bg-light dark p10 mb5 br2 br-a br-greyer">{props.children}</div>;
}

export function EditPanel(props: { dark?: boolean; border?: boolean; noPadding?: boolean; bottomMargin?: boolean; children: React.ReactNode }) {
    const classValue = classNames({
        p10: !props.noPadding,
        'bg-light': true,
        darker: props.dark,
        mb10: props.bottomMargin,
        'br-a': props.border,
        bw2: props.border,
        'br-grey': props.border,
    });
    return <div className={classValue}>{props.children}</div>;
}

export function EditPanelSection(props: { topBorder?: boolean; children: React.ReactNode }) {
    return (
        <>
            {props.topBorder && <hr className="short" />}
            <div className="section">{props.children}</div>
        </>
    );
}

export function EditPanelLabel(props: { rowCount: number; children: React.ReactNode }) {
    const classColumn = `field-label col-md-${props.rowCount.toString()}`;
    return <label className={classColumn}>{props.children} </label>;
}

export function EditPanelRow(props: { fixWidth?: boolean; children: React.ReactNode }) {
    const classValue = classNames({
        section: true,
        row: true,
        mw1000: props.fixWidth,
    });
    return <div className={classValue}>{props.children}</div>;
}

export function EditPanelCell(props: { rowCount: number; children?: React.ReactNode }) {
    const classColumn = `col-md-${props.rowCount.toString()}`;
    return <div className={classColumn}>{props.children}</div>;
}
export function EditPanelCellLabel(props: { rowCount?: number | undefined; text: string; tooltip?: string | undefined; shouldAddColon?: boolean }) {
    const classColumn = `col-md-${(props.rowCount ?? 1).toString()}`;
    const { shouldAddColon = props.text.endsWith('?') ? '' : ':' } = props;
    return (
        <div className={classColumn}>
            <strong>
                {props.text}
                {shouldAddColon && <>:</>}
            </strong>
            {props.tooltip && <ToolTip text={props.tooltip} />}
        </div>
    );
}

export function DataPanelRow(props: { children: React.ReactNode }) {
    return <div className="row section mt5">{props.children}</div>;
}

export function DataPanelCell(props: { rowCount: number; text: string; pt?: boolean; children: React.ReactNode }) {
    let classColumn = `col-md-${props.rowCount.toString()}`;
    if (props.pt) {
        classColumn += ' pt30';
    }
    return (
        <div className={classColumn}>
            <span className="mr10 fw600">{props.text}:</span> {props.children}
        </div>
    );
}

export function EditPanelButtons(props: { footer?: boolean; fixWidth?: boolean; topBorder?: boolean; children: React.ReactNode }) {
    const classValue = classNames({
        'text-right': true,
        mb5: !props.footer,
        'panel-footer': props.footer,
        mw1000: props.fixWidth,
        pr20: props.fixWidth,
    });
    return (
        <>
            {props.topBorder && <hr className="short" />}
            <div className={classValue}>{props.children}</div>
        </>
    );
}

export function EditPanelButton(props: {
    text: string;
    small?: undefined | boolean;
    primary?: undefined | boolean;
    danger?: undefined | boolean;
    disabled?: undefined | boolean;
    noLeftMargin?: undefined | boolean;
    onClick: () => void;
    iconClass?: undefined | string;
}) {
    const classValue = classNames({
        btn: true,
        'btn-sm': props.small,
        fs12: props.small,
        'btn-default': !props.primary || !props.danger,
        'btn-primary': props.primary,
        'btn-danger': props.danger,
        ml10: !props.noLeftMargin,
    });
    // return <a href="#" onClick={(e) => { e.preventDefault(); props.onClick(); }} className={classValue}>{props.text}</a>;
    // return <input type="button" disabled={props.disabled} className={classValue} onClick={(e) => { e.preventDefault(); props.onClick(); }} value={props.text} />;

    return (
        <button
            type="button"
            disabled={props.disabled}
            className={classValue}
            onClick={(e) => {
                e.preventDefault();
                props.onClick();
            }}
        >
            {props.iconClass && <span className={`fa ${props.iconClass} pr5`}></span>}
            {props.text}
        </button>
    );
}

export function EditPanelHeader(props: { text: string }) {
    return (
        <div className="section-divider mt30 mb20">
            <span>{props.text}</span>
        </div>
    );
}

export function EditPanelInput(props: {
    name: string;
    placeholder?: undefined | string;
    icon?: undefined | string;
    value: any;
    topLabel?: undefined | string;
    topLabelTooltip?: undefined | string;
    restrictToNumbers?: undefined | boolean;
    maxlength?: undefined | number;
    autoFocus?: undefined | boolean;
    disabled?: undefined | boolean;
    notValid?: undefined | boolean;
    autoComplete?: undefined | string;
    handleChange: (value: string, name: string) => void;
    handleBlur?: undefined | ((name: string) => void);
    handleEnter?: undefined | (() => void);
    handleEsc?: undefined | (() => void);
    'data-testid'?: undefined | string;
}) {
    const classValue = classNames({
        field: true,
        'prepend-icon': props.icon,
        mt5: props.topLabel,
        'state-error': props.notValid,
    });

    return (
        <>
            {props.topLabel && (
                <span className="ml5">
                    {props.topLabel}
                    {ToolTip({ text: props.topLabelTooltip })}
                </span>
            )}
            <label htmlFor={props.name} className={classValue}>
                <input
                    disabled={props.disabled}
                    type={props.restrictToNumbers ? 'number' : 'text'}
                    name={props.name}
                    id={props.name}
                    className="gui-input"
                    placeholder={props.placeholder ?? ''}
                    value={props.value || ''}
                    autoComplete={props.autoComplete}
                    maxLength={props.maxlength}
                    autoFocus={props.autoFocus}
                    onChange={(e) => props.handleChange(e.target.value, props.name)}
                    onBlur={() => {
                        if (props.handleBlur) {
                            props.handleBlur(props.name);
                        }
                    }}
                    onKeyUp={(e) => {
                        if (e.key === 'Enter' && props.handleEnter) {
                            props.handleEnter();
                        } else if (e.key === 'Escape' && props.handleEsc) {
                            props.handleEsc();
                        }
                    }}
                    data-testid={props['data-testid']}
                />
                {props.icon && (
                    <label htmlFor={props.name} className="field-icon">
                        <i className={props.icon}></i>
                    </label>
                )}
            </label>
        </>
    );
}

export function EditPanelInputPassword(props: {
    name: string;
    placeholder?: string;
    value: any;
    notValid?: boolean;
    autoComplete?: string;
    handleChange: (value: string, name: string) => void;
    handleEnter?: () => void;
}) {
    const classValue = classNames({
        field: true,
        'prepend-icon': true,
        mt5: true,
        'state-error': props.notValid,
    });

    return (
        <>
            <label htmlFor={props.name} className={classValue}>
                <input
                    type="password"
                    name={props.name}
                    id={props.name}
                    className="gui-input"
                    placeholder={props.placeholder ?? ''}
                    value={props.value || ''}
                    autoComplete={props.autoComplete}
                    required
                    onChange={(e) => props.handleChange(e.target.value, props.name)}
                    onKeyUp={(e) => {
                        if (props.handleEnter && e.key === 'Enter') {
                            props.handleEnter();
                        }
                    }}
                />
                <label htmlFor={props.name} className="field-icon">
                    <i className="fa fa-lock"></i>
                </label>
            </label>
        </>
    );
}

type TextareaProps = {
    name: string;
    placeholder?: string;
    icon?: string;
    value: any;
    topLabel?: string;
    handleChange: (value: string, name: string) => void;
    handleBlur?: (name: string) => void;
    disabled?: boolean;
};

export function EditPanelTextArea(props: TextareaProps) {
    const classValue = classNames({
        field: true,
        'prepend-icon': props.icon,
    });

    return (
        <>
            {props.topLabel && <span className="ml5">{props.topLabel}</span>}
            <label htmlFor={props.name} className={classValue}>
                <textarea
                    name={props.name}
                    placeholder={props.placeholder ?? ''}
                    className="gui-textarea"
                    disabled={props.disabled}
                    onChange={(e) => props.handleChange(e.target.value, props.name)}
                    value={props.value || ''}
                />
                {props.icon && (
                    <label htmlFor={props.name} className="field-icon">
                        <i className={props.icon}></i>
                    </label>
                )}
            </label>
        </>
    );
}

export function EditPanelSwitch(props: {
    name: string;
    placeholder?: string | undefined;
    value: any;
    handleChange: (value: boolean, name: string) => void;
    topLabel?: string | undefined;
    toolTip?: string | undefined;
    disabled?: boolean | undefined;
    onText?: string | undefined;
    offText?: string | undefined;
    id?: string | undefined;
}) {
    const translate = useTranslate();
    const defaultOnText = translate('common.on');
    const defaultOffText = translate('common.off');
    const { disabled = false, onText = defaultOnText, offText = defaultOffText } = props;
    return (
        <>
            {props.topLabel && <span className="ml5">{props.topLabel}</span>}
            <label className={`switch block mt15 ${disabled ? 'disabled' : ''}`}>
                <input
                    disabled={disabled}
                    type="checkbox"
                    name={props.name}
                    id={props.id ? props.id : props.name}
                    value={props.name}
                    checked={props.value || false}
                    onChange={(e) => props.handleChange((e.target as any).checked, props.name)}
                />
                <label htmlFor={props.id ? props.id : props.name} data-on={onText} data-off={offText}></label>
                <span className="mt15">
                    {props.placeholder} {ToolTip({ text: props.toolTip })}
                </span>
            </label>
        </>
    );
}

type IEditPanelDatePickerProps = {
    name: string;
    placeholder?: string;
    value: any;
    topLabel?: string;
    handleChange: (value: string | null, name: string) => void;
    disabled?: boolean;
    notValid?: boolean;
};

export function EditPanelDatePicker(props: IEditPanelDatePickerProps) {
    const currentAppContext = React.useContext(appContext.context);
    const classes = classNames({
        'field prepend-icon': true,
        mt5: props.topLabel,
        'state-error': props.notValid,
    });

    return (
        <>
            {props.topLabel && <span className="ml5">{props.topLabel}</span>}
            <label htmlFor={props.name} className={classes}>
                <LocalizedDatePicker
                    id={props.name}
                    name={props.name}
                    parse={(input: any) => parseDateInput(input, currentAppContext.languageCode) ?? undefined}
                    placeholder={props.placeholder as string}
                    value={props.value ? moment(props.value).toDate() : null}
                    onChange={(date: any) => {
                        if (date && moment.isDate(date)) {
                            props.handleChange(moment(date).format('YYYY-MM-DD'), props.name);
                        } else {
                            props.handleChange(null, props.name);
                        }
                    }}
                    disabled={props.disabled ?? false}
                    onKeyPress={(event: any) => {
                        if (event.key === 'Enter') {
                            const date = parseDateInput(event.target.value, currentAppContext.languageCode);
                            if (date) {
                                props.handleChange(moment(date).format('YYYY-MM-DD'), props.name);
                            } else {
                                props.handleChange(null, props.name);
                            }
                        }
                    }}
                />
            </label>
        </>
    );
}

export function EditPanelTime(props: {
    name: string;
    placeholder?: string;
    icon?: string;
    value: any;
    topLabel?: string;
    topLabelTooltip?: string;
    isTimeDuration?: string;
    notValid?: boolean;
    handleChange: (value: string | null, name: string) => void;
    handleBlur?: () => void;
    handleEnter?: () => void;
    disabled?: boolean;
}) {
    const [isValidTime, setIsValidTime] = React.useState<boolean>(true);
    const classValue = classNames({
        field: true,
        'prepend-icon': props.icon,
        mt5: props.topLabel,
        'state-error': !isValidTime,
    });

    return (
        <>
            {props.topLabel && (
                <span className="ml5">
                    {props.topLabel}
                    {ToolTip({ text: props.topLabelTooltip })}
                </span>
            )}
            <label htmlFor={props.name} className={classValue}>
                <input
                    disabled={props.disabled}
                    type="text"
                    name={props.name}
                    id={props.name}
                    className="gui-input"
                    placeholder={props.placeholder ?? ''}
                    value={props.value || ''}
                    maxLength={5}
                    onChange={(e) => {
                        props.handleChange(e.target.value, props.name);
                        // if (timeEntered && moment.isDate(timeEntered)) {
                        //     props.handleChange(moment(timeEntered).format('YYYY-MM-DD'), props.name);
                        // } else {
                        //     props.handleChange(null, props.name);
                        // }
                    }}
                    onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                        const {
                            target: { value },
                        } = event;
                        const parsedTime = parseTime(value);

                        if (value && !parsedTime) {
                            setIsValidTime(false);
                        } else if (value && parsedTime) {
                            props.handleChange(parsedTime, props.name);
                        }

                        if (props.handleBlur) {
                            props.handleBlur();
                        }
                    }}
                    onKeyUp={(e) => {
                        if (props.handleEnter && e.key === 'Enter') {
                            props.handleEnter();
                        }
                    }}
                />
                {props.icon && (
                    <label htmlFor={props.name} className="field-icon">
                        <i className={props.icon}></i>
                    </label>
                )}
            </label>
        </>
    );
}

function parseDateInput(input: string, languageCode = 'nl') {
    return parseDate(input, languageCode);
}

// for when someone puts a data in like '020301' it will parse to 02-03-2001 or 03/02/2001
export function parseDate(input: string, languageCode: string) {
    if (input === '') {
        // DateTimePicker does not accept undefined/null/'' as parse value
        return null;
    }

    let parsedDate;
    if (!isNaN(input as any)) {
        if (input.length !== 6 && input.length !== 8) {
            return null;
        }
        let year;
        if (input.length === 6) {
            // Bug if year needs to be >= 2100
            year = Number(`20${input.slice(4, 6)}`);
            // if (birthDate && year > moment().year()) {
            //     year = year - 100;
            // }
        } else {
            year = input.slice(4, 8);
        }

        switch (languageCode) {
            case 'nl':
                parsedDate = `${year}-${input.slice(2, 4)}-${input.slice(0, 2)}`;
                break;
            case 'en':
            default:
                parsedDate = `${year}-${input.slice(0, 2)}-${input.slice(2, 4)}`;
        }
    } else {
        const splitInput = input.split(/\D/);
        if (splitInput.length !== 3) {
            return null;
        }

        switch (languageCode) {
            case 'nl':
                parsedDate = `${splitInput[2]}-${convertToTowDigits(splitInput[1]!)}-${convertToTowDigits(splitInput[0]!)}`;
                break;
            case 'en':
            default:
                parsedDate = `${splitInput[2]}-${convertToTowDigits(splitInput[0]!)}-${convertToTowDigits(splitInput[1]!)}`;
        }
        if (!parsedDate.match(/^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/)) {
            return null;
        }
    }
    const checkDate = moment(parsedDate);
    if (checkDate.isValid()) {
        // Needs to return a Date and make sure localTime is used
        return moment(parsedDate).utc(true).toDate();
    }
    return null;
}

export function parseTime(input: string) {
    if (input === '') {
        // DateTimePicker does not accept undefined/null/'' as parse value
        return null;
    }

    let parsedTime = input;
    if (input.length < 3 && input.length > 5) {
        return null;
    }
    if (!isNaN(input as any)) {
        if (input.length === 3) {
            parsedTime = `0${input.slice(0, 1)}:${input.slice(1, 3)}`;
        } else if (input.length === 4) {
            if (input.includes(':')) {
                parsedTime = input;
            } else {
                parsedTime = `${input.slice(0, 2)}:${input.slice(2, 4)}`;
            }
        } else {
            parsedTime = input;
        }
    }

    const isValid = /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/.test(parsedTime);
    if (isValid) {
        // Needs to return a Date and make sure localTime is used
        return parsedTime;
    }
    return null;
}

// ISO format only accepts '01' and not '1'
function convertToTowDigits(input: string) {
    return input.length === 1 ? `0${input}` : input;
}

export interface ISelectOption {
    value: string;
    text: string;
}

export function EditPanelSelect(props: {
    name: string;
    placeholder?: undefined | string;
    value: any;
    disabled?: undefined | boolean;
    options: ISelectOption[];
    topLabel?: undefined | string;
    topLabelTooltip?: undefined | string;
    notValid?: undefined | boolean;
    handleChange: (value: string, name: string) => void;
    handleBlur?: undefined | ((name: string) => void);
}) {
    const classValue = classNames({
        field: true,
        select: true,
        mt5: props.topLabel,
        'state-error': props.notValid,
    });

    return (
        <>
            {props.topLabel && (
                <span className="ml5">
                    {props.topLabel}
                    {ToolTip({ text: props.topLabelTooltip })}
                </span>
            )}
            <label className={classValue}>
                <select
                    id={props.name}
                    name={props.name}
                    disabled={props.disabled}
                    value={props.value || ''}
                    onBlur={() => {
                        if (props.handleBlur) {
                            props.handleBlur(props.name);
                        }
                    }}
                    onChange={(e) => props.handleChange(e.target.value, props.name)}
                >
                    {props.placeholder && <option value="">{props.placeholder}</option>}
                    {props.options.map((option, i) => (
                        <option key={i} value={option.value}>
                            {option.text}
                        </option>
                    ))}
                </select>
                <i className="arrow" />
            </label>
        </>
    );
}

export function EditPanelFilterSelect(props: {
    name: string;
    placeholder?: string | undefined;
    notSelectedText?: string | undefined;
    value: any;
    disabled?: boolean | undefined;
    options: ISelectOption[];
    topLabel?: string | undefined;
    notValid?: boolean | undefined;
    handleChange: (value: string, name: string) => void;
}) {
    const translate = useTranslate();
    const classValue = classNames({
        field: true,
        select: true,
        mb10: true,
        mt5: props.topLabel,
        'state-error': props.notValid,
    });

    let allOptions = props.options;
    if (props.notSelectedText) {
        allOptions = [{ value: '', text: props.notSelectedText }].concat(allOptions);
    }

    const placeholder = props.placeholder ?? `${translate('common.select')}...`;

    const selectOptions = allOptions.map((option) => ({ value: option.value, label: option.text }));

    return (
        <>
            {props.topLabel && <span className="ml5">{props.topLabel}</span>}
            <label className={classValue} style={{ height: '30px' }}>
                <Select
                    styles={reactSelectDefaultStyles}
                    key={props.value}
                    isClearable={true}
                    filterOption={(option, filter) => (option.label ? option.label : '').toLowerCase().indexOf(filter.toLowerCase()) !== -1}
                    menuPortalTarget={document.body}
                    placeholder={placeholder}
                    name={props.name}
                    isDisabled={props.disabled ?? false}
                    value={props.value ? selectOptions.find((i) => i.value === props.value) : undefined}
                    options={selectOptions}
                    onChange={(option: any) => {
                        props.handleChange(option ? (option.value ? (option.value === 'null' ? null : option.value) : null) : null, props.name);
                    }}
                />
            </label>
        </>
    );
}
type EditPanelFilterMultiSelectProps = {
    name: string;
    placeholder?: undefined | string;
    notSelectedText?: undefined | string;
    value: string[];
    disabled?: undefined | boolean;
    options: ISelectOption[];
    topLabel?: undefined | string;
    notValid?: undefined | boolean;
    handleChange: (value: string[], name: string) => void;
};

export function EditPanelFilterMultiSelect(props: EditPanelFilterMultiSelectProps) {
    const classValue = classNames({
        field: true,
        select: true,
        mb10: true,
        mt5: props.topLabel,
        'state-error': props.notValid,
    });

    let allOptions = props.options;
    if (props.notSelectedText) {
        allOptions = [{ value: '', text: props.notSelectedText }].concat(allOptions);
    }

    const selectOptions = allOptions.map((option) => ({ value: option.value, label: option.text }));

    return (
        <>
            {props.topLabel && <span className="ml5">{props.topLabel}</span>}
            <label className={classValue} style={{ height: '30px' }}>
                <Select
                    isClearable={true}
                    isMulti={true}
                    filterOption={(option, filter) => (option.label ? option.label : '').toLowerCase().indexOf(filter.toLowerCase()) !== -1}
                    menuPortalTarget={document.body}
                    styles={reactSelectDefaultStyles}
                    placeholder={props.placeholder}
                    name={props.name}
                    isDisabled={props.disabled ?? false}
                    value={props.value ? selectOptions.filter((i) => props.value.includes(i.value)) : undefined}
                    options={selectOptions}
                    onChange={(options: any) => {
                        props.handleChange(options === null ? [] : options.map((i: { value: string; label: string }) => i.value), props.name);
                    }}
                />
            </label>
        </>
    );
}

export interface ILabelOption {
    value: string;
    label: string;
}

type EditPanelFilterCreateSelectProps = {
    name: string;
    placeholder?: string;
    value: null | string[];
    options: string[];
    topLabel?: string;
    notValid?: boolean;
    handleChange: (value: string[], name: string) => void;
    disabled?: boolean;
};

export function EditPanelFilterCreateSelect(props: EditPanelFilterCreateSelectProps) {
    const selectStyles = { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }), menuList: (styles: any) => ({ ...styles, zIndex: 999 }) };
    const classValue = classNames({
        field: true,
        select: true,
        mt5: props.topLabel,
        'state-error': props.notValid,
    });

    const transformValues = (passedValues: null | string[]): null | ILabelOption[] => {
        if (passedValues === null) {
            return passedValues;
        }

        return passedValues.map((value: string) => ({ value, label: value }));
    };

    const selectOptions = transformValues(props.options);
    const selectValue = transformValues(props.value);

    const handleBlurEvent = (event: React.MouseEvent<HTMLInputElement>) => {
        const {
            currentTarget: { value },
        } = event;
        const isLabelsEmpty = Array.isArray(props.value) && props.value.length === 0;
        const isLabelEntered = typeof value === 'string' && value.length > 0;

        // typed but not 'created' label will be appended to 'created' list
        // only if user clicked on submit/create button
        if (isLabelsEmpty && isLabelEntered) {
            props.handleChange([value], props.name);
        }
    };

    const changeHandler = (options: null | ILabelOption[]) => {
        const value = options === null ? [] : options.map((option: ILabelOption) => option.value);

        props.handleChange(value, props.name);
    };

    return (
        <>
            {props.topLabel && <span className="ml5">{props.topLabel}</span>}
            <label className={classValue} style={{ height: '30px' }}>
                <Creatable
                    isClearable={true}
                    isMulti={true}
                    name={props.name}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    filterOption={(option: ILabelOption, filter: string) =>
                        (option.label ? option.label : '').toLowerCase().indexOf(filter.toLowerCase()) !== -1
                    }
                    placeholder={props.placeholder}
                    value={selectValue}
                    options={selectOptions}
                    onBlur={handleBlurEvent}
                    onChange={changeHandler}
                    isDisabled={props.disabled}
                />
            </label>
        </>
    );
}

export function SmallFilterSelect(props: {
    name: string;
    placeholder?: string;
    value: any;
    disabled?: boolean;
    options: ISelectOption[];
    topLabel?: string;
    topLabelTooltip?: string;
    handleChange: (value: string, name: string) => void;
}) {
    // let allOptions = props.options;

    const selectOptions = props.options.map((option) => ({ value: option.value, label: option.text }));

    return (
        <>
            {props.topLabel && (
                <span className="ml5">
                    {props.topLabel}
                    {ToolTip({ text: props.topLabelTooltip })}
                </span>
            )}
            <Select
                isClearable={true}
                styles={reactSelectDefaultStyles}
                menuPortalTarget={document.body}
                filterOption={(option, filter) => (option.label ? option.label : '').toLowerCase().indexOf(filter.toLowerCase()) !== -1}
                placeholder={props.placeholder}
                name={props.name}
                isDisabled={props.disabled ?? false}
                value={props.value ? selectOptions.find((i) => i.value === props.value) : undefined}
                options={selectOptions}
                onChange={(option: any) => {
                    props.handleChange(option ? (option.value ? (option.value === 'null' ? null : option.value) : null) : null, props.name);
                }}
            />
        </>
    );
}

export function SmallSelect(props: {
    name: string;
    placeholder?: string;
    value: any;
    disabled?: boolean;
    options: ISelectOption[];
    topLabel?: string;
    handleChange: (value: string, name: string) => void;
}) {
    return (
        <>
            {props.topLabel && <span className="ml5">{props.topLabel}</span>}
            <select
                id={props.name}
                name={props.name}
                disabled={props.disabled}
                className="form-control input-md"
                value={props.value || ''}
                onChange={(e) => props.handleChange(e.target.value, props.name)}
            >
                {props.placeholder && <option value="">{props.placeholder}</option>}
                {props.options.map((option, i) => (
                    <option key={i} value={option.value}>
                        {option.text}
                    </option>
                ))}
            </select>
        </>
    );
}

export function InputSearchText(props: {
    name: string;
    placeholder?: string;
    value: any;
    disabled?: boolean;
    topLabel?: string;
    handleChange: (value: string, name: string) => void;
    handleEnter?: () => void;
}) {
    const [text, setText] = React.useState(props.value);

    React.useEffect(() => setText(props.value), [props.value]);

    return (
        <>
            {props.topLabel && <span className="ml5">{props.topLabel}</span>}
            <input
                type="text"
                id={props.name}
                name={props.name}
                disabled={props.disabled}
                className="form-control input-md"
                value={text || ''}
                onChange={(e) => setText(e.target.value)}
                onBlur={() => props.handleChange(text, props.name)}
                onKeyDown={(e) => {
                    if (props.handleEnter && e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        props.handleChange(text, props.name);
                        props.handleEnter();
                    }
                }}
                placeholder={props.placeholder}
            />
        </>
    );
}

export function InputRadioButtons(props: {
    name: string;
    placeholder?: string;
    value: any;
    disabled?: boolean;
    topLabel?: string;
    options: ISelectOption[];

    handleChange: (value: string, name: string) => void;
}) {
    return (
        <>
            {props.topLabel && <span>{props.topLabel}</span>}
            <div className="mt5">
                {props.options.map((option) => (
                    <div className="form-check mb5 ml5" key={option.value}>
                        <label>
                            <input
                                disabled={props.disabled}
                                type="radio"
                                name={props.name}
                                value={option.value}
                                checked={props.value === option.value}
                                className="form-check-input mr10"
                                onChange={() => props.handleChange(option.value, props.name)}
                            />
                            {option.text}
                        </label>
                    </div>
                ))}
            </div>
        </>
    );
}

type AccordionProps = {
    title: string;
    index?: string;
    children: React.ReactNode;
    name?: string;
};

export function Accordion(props: AccordionProps) {
    const headerOptions = { text: props.title, initialIsClosed: true, noDark: true };
    return (
        <div className="mb20">
            <CollapsiblePanel heading={headerOptions} name={props.name}>
                <PanelBody isForm={true} whiteBackground={true}>
                    {props.children}
                </PanelBody>
            </CollapsiblePanel>
        </div>
    );
}

export function PopupElement(props: { small?: boolean; xsmall?: boolean; onClick?: () => void; children: React.ReactNode }) {
    const classValue = classNames({
        mw600: props.small,
        mw400: props.xsmall,
    });

    return (
        <>
            <div id="popup_element" className={classValue}>
                {props.children}
            </div>
            <div id="background_popup" style={{ opacity: 0.6, display: 'block' }} onClick={props.onClick} />
        </>
    );
}

export function ScrollMenu(props: { children: React.ReactNode }) {
    return (
        <div id="nav-spy">
            <ul className="nav tray-nav">{props.children}</ul>
        </div>
    );
}

export function ScrollMenuItem(props: { text: string; icon: string; onClick: () => void }) {
    return (
        <li>
            <a
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    props.onClick();
                }}
            >
                <span className={`${props.icon} fa-lg fa-fw mr5`}></span> {props.text}
            </a>
        </li>
    );
}

interface IInputNumberProps {
    placeholder?: string | undefined;
    name: string;
    value?: number | undefined;
    onChange: (value: number | null) => void;
    onBlur?: (isChanged: boolean) => Promise<void>;
    decimalSeparator: string;
    'data-testid'?: string | undefined;
    className?: string | undefined;
    unit?: string | undefined;
}

interface IInputNumberState {
    value: string;
    lastCorrectValue?: undefined | number | null;
    isChanged: boolean;
}

export class InputNumber extends Component<IInputNumberProps, IInputNumberState> {
    constructor(props: IInputNumberProps) {
        super(props);

        const transformedValue = this.valueToString(props.value, this.props.decimalSeparator);
        this.state = {
            value: transformedValue,
            isChanged: false,
            lastCorrectValue: props.value,
        };
    }

    public override async componentDidUpdate(prevProps: IInputNumberProps) {
        if (prevProps.value !== this.props.value) {
            await this.setState({ value: this.valueToString(this.props.value, this.props.decimalSeparator) });
        }
    }

    public override render() {
        return (
            <div className="input-group">
                <input
                    inputMode="decimal"
                    formNoValidate
                    name={this.props.name}
                    value={this.state.value}
                    style={{ paddingRight: '20px', textAlign: 'right' }}
                    className={this.props.className ?? '' + ' form-control'}
                    placeholder={this.props.placeholder}
                    data-testid={this.props['data-testid']}
                    onChange={async (e) => {
                        const newValue = e.target.value;
                        await this.setState({ value: newValue });

                        let newTransformedValue = newValue;
                        if (this.props.decimalSeparator === ',') {
                            newTransformedValue = newValue.replace('.', '').replace(',', '.');
                        }

                        if (newTransformedValue === '' || !isNaN(Number(newTransformedValue))) {
                            await this.setState({ isChanged: true });

                            if (newTransformedValue === '') {
                                void this.props.onChange(null);

                                await this.setState({ lastCorrectValue: null });
                            } else {
                                void this.props.onChange(Number(newTransformedValue));
                                await this.setState({ lastCorrectValue: Number(newTransformedValue) });
                            }
                        }
                    }}
                    onBlur={async () => {
                        let newTransformedValue = this.state.value;
                        if (this.state.value === '') {
                            void this.props.onChange(null);
                        } else {
                            if (this.props.decimalSeparator === ',') {
                                newTransformedValue = this.state.value.replace('.', '').replace(',', '.');
                            }

                            if (!isNaN(Number(newTransformedValue))) {
                                void this.props.onChange(Number(newTransformedValue));
                            } else {
                                void this.props.onChange(null);
                            }
                        }
                        await this.props.onBlur?.(this.state.isChanged);
                    }}
                />
                {this.props.unit && <span className="input-group-addon">{this.props.unit}</span>}
            </div>
        );
    }

    private valueToString(value?: number, decimalSeperator?: string) {
        return value ? value.toString().replace('.', decimalSeperator ?? '.') : '';
    }
}

interface ITableRowAction {
    text: string;
}

interface ILinkAction extends ITableRowAction {
    url: string;
}
interface IClickAction extends ITableRowAction {
    onClick: () => void;
}

export function TableRowActions(props: { actions: (ILinkAction | IClickAction)[] }) {
    const currentAppContext = React.useContext(appContext.context);
    const translateKey = getTranslate(currentAppContext.languageCode);
    const navigate = useNavigate();
    const { buttonProps, itemProps, isOpen, setIsOpen } = usePatchedDropdownMenu(props.actions.length);
    const classes = classNames('btn-group text-right', { open: isOpen });

    return (
        <div className={classes}>
            <button {...buttonProps} className="btn btn-primary br2 btn-xs fs12 dropdown-toggle" data-toggle="dropdown" type="button">
                {translateKey('common.actions')}
                <span className="caret ml5"></span>
            </button>
            <ul className="dropdown-menu" role="menu">
                {props.actions.map((action, index) => (
                    <li
                        key={index}
                        {...itemProps[index]}
                        onClick={() => {
                            setIsOpen(false);

                            if (Object.prototype.hasOwnProperty.call(action, 'url')) {
                                navigate((action as ILinkAction).url);
                            } else {
                                (action as IClickAction).onClick();
                            }
                        }}
                    >
                        <a className="cursor">{action.text}</a>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function Alert(props: { severity: 'info' | 'warning' | 'danger'; children: React.ReactNode }) {
    return (
        <div className={`alert alert-dismissable alert-${props.severity}`}>
            <button type="button" className="close" data-dismiss="alert" aria-hidden="true">
                ×
            </button>
            {props.children}
        </div>
    );
}

export function AlertMicro(props: { severity: 'info' | 'warning' | 'danger'; text?: string; children?: React.ReactNode }) {
    return (
        <div className={`alert alert-micro alert-border-left alert-${props.severity} mn`}>
            <i className="fa fa-info pr10"></i>
            {props.text}
            {props.children}
        </div>
    );
}

export function TextWithLineBreaks(props: { text: string | undefined | null }) {
    if (!props.text) {
        return null;
    }
    return (
        <>
            {props.text.split('\n').map((s, i) => (
                <React.Fragment key={i}>
                    {s}
                    <br />
                </React.Fragment>
            ))}
        </>
    );
}

export function EditPanelExplanation(props: { text: string }) {
    return (
        <div className="fs12 text-muted-darker mt5">
            <i>{props.text}</i>
        </div>
    );
}

export function LoginHeader(props: { children: React.ReactNode }) {
    return (
        <div className="panel-header">
            <div className="pl25 pt5 pb5">
                <h2 className="h3 fw200">{props.children}</h2>
            </div>
        </div>
    );
}

export function ToolTip(props: { text: string | undefined }) {
    if (!props.text) {
        return null;
    }
    return (
        <span className="tooltipZXY">
            <i className="fa fa-info fs12 text-info ml10" style={{ color: '#5196d6' }} />
            <span className="tooltiptext">{props.text}</span>
        </span>
    );
}
